import React, { useState, useEffect } from 'react';
import axios from 'axios';

function UserManagement({ currentUser, isStaging }) {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    is_active: true
  });

  const authHeaders = () => ({
    'X-Organization': (currentUser?.organization || '').trim(),
    'X-Username': (currentUser?.username || '').trim(),
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadUsers();
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async () => {
    setLoading(true);
    setLoadError(null);
    const org = (currentUser?.organization || '').trim();
    const u = (currentUser?.username || '').trim();
    try {
      const response = await axios.get('/api/users', {
        headers: authHeaders(),
        params: { organization: org, username: u }
      });
      setAllUsers(response.data.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setAllUsers([]);
      const msg = err.response?.data?.error || err.message || 'Could not load users';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isDachido = (currentUser?.organization || '').trim() === 'Dachido';
  const users = isDachido
    ? allUsers
    : allUsers.filter(u => u.organization === currentUser?.organization);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pwd = (formData.password || '').trim();
    if (pwd.length > 0 && pwd.length <= 6) {
      alert('Password must be longer than 6 characters.');
      return;
    }
    if (!editingUser && pwd.length === 0) {
      alert('Password is required.');
      return;
    }
    try {
      const headers = authHeaders();

      if (editingUser) {
        await axios.put(`/api/users/${editingUser.user_id}`, {
          ...formData,
          admin_organization: currentUser.organization,
          admin_username: currentUser.username
        }, { headers });
      } else {
        await axios.post('/api/users', {
          ...formData,
          organization: currentUser.organization,
          admin_organization: currentUser.organization,
          admin_username: currentUser.username
        }, { headers });
      }
      await loadUsers();
      setShowModal(false);
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        role: 'user',
        is_active: true
      });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save user';
      alert(msg);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      is_active: user.is_active === 1
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/users/${userId}`, {
        headers: authHeaders(),
        data: {
          admin_organization: currentUser.organization,
          admin_username: currentUser.username
        }
      });
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      role: 'user',
      is_active: true
    });
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
        <h3>Access Denied</h3>
        <p>Admin access required to view user management.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2>User Management</h2>
          <p>Manage users, roles, and permissions</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setEditingUser(null);
            setFormData({
              username: '',
              password: '',
              role: 'user',
              is_active: true
            });
            setShowModal(true);
          }}
        >
          + Add User
        </button>
      </div>

      {loadError && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#856404' }}>{loadError}</span>
          <button type="button" className="btn btn-sm btn-primary" onClick={loadUsers}>
            Retry
          </button>
        </div>
      )}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading users...</div>
      ) : (
        <div className="card">
          <div className="card-body">
            <table className="data-table">
              <thead>
                <tr>
                  {isDachido && <th>Organization</th>}
                  <th>Username</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.user_id}>
                    {isDachido && <td>{user.organization || '-'}</td>}
                    <td>{user.username}</td>
                    <td>
                      <span className={`badge ${user.role === 'admin' ? 'info' : user.role === 'app' ? 'primary' : user.role === 'app_staging' ? 'warning' : 'secondary'}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${user.is_active === 1 ? 'success' : 'warning'}`}>
                        {user.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                    <td>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(user)}
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(user.user_id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span>{editingUser ? 'Edit User' : 'Add New User'}</span>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingUser}
                  placeholder="Enter username"
                />
                {editingUser && <small style={{ color: '#999' }}>Username cannot be changed</small>}
              </div>

              <div className="form-group">
                <label>Password {editingUser && '(leave empty to keep current)'}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  placeholder="Enter password"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="app">App</option>
                </select>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
